/**
 * 날씨 응답 → 화면이 그릴 값 (2026-09-21 · 시안 v4 확정).
 *
 * 서버(`GET /weather/by-place`)는 기상청 범주 코드를 **그대로** 준다(`T1H` · `REH` · `PTY` · `SKY` · `POP` · `WSD`).
 * 그 코드를 사람이 읽는 값으로 바꾸는 곳은 **여기 하나**다 — 고정 줄과 위젯이 같은 값을 보게(시안 v4 의 약속).
 *
 * ## 어디서 무엇을 가져오나
 *
 *   · 지금 기온 · 습도 · 바람 — **실황(ncst)**. 실황에는 하늘 상태(`SKY`)가 없다.
 *   · 하늘 · 강수 확률 — **초단기예보(vsrt)의 첫 시각**. 그래서 「구름 조금」과 「강수 20%」는 예보값이다.
 *   · 시간별 예보 — 초단기예보의 series(1시간 간격 · 최대 6시간).
 *
 * ## 체감 온도는 우리가 셈한다
 *
 * 기상청 응답에 체감 온도 칸이 없다. 그래서 **공표된 두 식**으로 셈한다:
 *   · 기온 10℃ 이하 · 바람 1.3m/s 이상 → **바람냉각(wind chill)**
 *   · 기온 27℃ 이상 → **열지수(heat index · 습도 필요)**
 *   · 그 사이 → 기온 그대로
 * 둘 다 셈할 수 없으면(바람·습도가 없으면) **체감을 그리지 않는다** — 모르는 값을 지어내지 않는다.
 */

/** 기상청 강수형태(PTY) — 실황·예보 공통. */
const PTY_LABEL: Readonly<Record<string, string>> = {
  0: '', 1: '비', 2: '비/눈', 3: '눈', 4: '소나기', 5: '빗방울', 6: '빗방울/눈날림', 7: '눈날림',
};
/** 기상청 하늘상태(SKY) — 예보에만 있다. */
const SKY_LABEL: Readonly<Record<string, string>> = { 1: '맑음', 3: '구름 많음', 4: '흐림' };

export interface WeatherNow {
  /** 섭씨 기온. 없으면 null — 그때는 위젯이 「가져오지 못했습니다」로 간다. */
  tempC: number | null;
  /** 「맑음」 · 「구름 많음」 · 「비」 … 모르면 null. */
  sky: string | null;
  /** 셈한 체감 온도. 셈할 수 없으면 null. */
  feelsC: number | null;
  /** 상대습도 %. */
  humidity: number | null;
  /** 강수 확률 %(초단기예보). */
  pop: number | null;
  /** 풍속 m/s. */
  windMs: number | null;
}

export interface WeatherHour {
  /** 「15시」 */
  label: string;
  tempC: number | null;
  sky: string | null;
  pop: number | null;
}

export interface WeatherView {
  now: WeatherNow;
  hours: WeatherHour[];
}

type KindResult = {
  kind: string;
  status: string;
  baseAt: string | null;
  reason: string | null;
  summary: { now?: Record<string, string>; series?: Array<{ at: string; values: Record<string, string> }> } | null;
};

const num = (v: string | undefined): number | null => {
  if (v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** 기상청 공표식 — 바람냉각 온도(℃). 기온 10℃ 이하 · 풍속 1.3m/s 이상에서만 뜻이 있다. */
export function windChillC(tempC: number, windMs: number): number {
  const v = (windMs * 3.6) ** 0.16; // km/h 로 바꿔 지수
  return 13.12 + 0.6215 * tempC - 11.37 * v + 0.3965 * tempC * v;
}

/** 기상청 공표식 — 열지수(℃). 기온 27℃ 이상 · 습도가 있어야 한다. */
export function heatIndexC(tempC: number, humidity: number): number {
  const t = tempC;
  const r = humidity;
  return -8.784695 + 1.61139411 * t + 2.338549 * r - 0.14611605 * t * r
    - 0.012308094 * t * t - 0.016424828 * r * r + 0.002211732 * t * t * r
    + 0.00072546 * t * r * r - 0.000003582 * t * t * r * r;
}

/** 셈할 수 있을 때만 체감을 준다 — 모르면 null. */
export function feelsLikeC(tempC: number | null, windMs: number | null, humidity: number | null): number | null {
  if (tempC === null) return null;
  if (tempC <= 10 && windMs !== null && windMs >= 1.3) return Math.round(windChillC(tempC, windMs) * 10) / 10;
  if (tempC >= 27 && humidity !== null) return Math.round(heatIndexC(tempC, humidity) * 10) / 10;
  return tempC;
}

/** `202609211500` → 「15시」. 모양이 다르면 null. */
export function hourLabel(at: string): string | null {
  const m = /^\d{8}(\d{2})\d{2}$/.exec(at);
  return m ? `${Number(m[1])}시` : null;
}

function skyOf(values: Record<string, string> | undefined): string | null {
  if (!values) return null;
  const pty = PTY_LABEL[String(num(values.PTY) ?? '')];
  if (pty) return pty;
  return SKY_LABEL[String(num(values.SKY) ?? '')] ?? null;
}

/**
 * 서버 응답의 `weather.results` → 화면 값.
 * ★ 실황이 없으면 `now.tempC` 가 null 이고, 부르는 쪽은 그것을 「값을 못 가져옴」으로 읽는다.
 */
export function toWeatherView(results: readonly KindResult[] | null | undefined, maxHours = 6): WeatherView {
  const ncst = results?.find((r) => r.kind === 'ncst')?.summary?.now;
  const series = results?.find((r) => r.kind === 'vsrt')?.summary?.series ?? [];
  const first = series[0]?.values;

  const tempC = num(ncst?.T1H);
  const humidity = num(ncst?.REH);
  const windMs = num(ncst?.WSD);
  // 실황에는 하늘 상태가 없다 — 비/눈 여부는 실황(PTY)이 먼저, 맑음·흐림은 예보에서.
  const nowPty = PTY_LABEL[String(num(ncst?.PTY) ?? '')] || null;

  return {
    now: {
      tempC,
      sky: nowPty ?? skyOf(first),
      feelsC: feelsLikeC(tempC, windMs, humidity),
      humidity,
      pop: num(first?.POP),
      windMs,
    },
    hours: series.slice(0, maxHours).map((s) => ({
      label: hourLabel(s.at) ?? '',
      tempC: num(s.values.T1H),
      sky: skyOf(s.values),
      pop: num(s.values.POP),
    })).filter((h) => h.label !== ''),
  };
}

/** 「21.3°」 — 없으면 「—」. */
export function tempText(v: number | null): string {
  return v === null ? '—' : `${Math.round(v * 10) / 10}°`;
}
